import {
  faChevronLeft,
  faChevronRight,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
// eslint-disable-next-line no-unused-vars
import { motion } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import data from "../../../backend/data.json";
import {
  createStaggerContainer,
  revealUp,
  sectionViewport,
  tapPress,
} from "../../utils/motionVariants";

/**
 * Animation variants pour l'apparition progressive des cartes de compétences
 */
const containerVariants = createStaggerContainer(0.08, 0.02);

const ICON_PREFIX_MAP = {
  "fa-brands": "fab",
  "fa-solid": "fas",
  "fa-regular": "far",
};

/**
 * Convertit une classe FontAwesome ("fa-brands fa-html5") en tuple
 * compatible avec le prop `icon` de react-fontawesome (["fab", "html5"]).
 */
function parseIconClass(iconClass) {
  if (!iconClass) return null;
  const [stylePart, namePart] = iconClass.split(" ");
  const prefix = ICON_PREFIX_MAP[stylePart];
  const name = namePart?.replace(/^fa-/, "");
  return prefix && name ? [prefix, name] : null;
}

/**
 * Composant Stack - Affiche les compétences techniques avec filtrage par catégorie
 * @component
 * @returns {JSX.Element} Carrousel interactif des compétences
 */
function Stack() {
  // État pour la catégorie de filtre active
  const [activeCategory, setActiveCategory] = useState("All");

  // Référence pour le carrousel
  const carouselRef = useRef(null);

  // Extraction des catégories uniques depuis les données
  const categories = [
    "All",
    ...new Set(data.stack.item.map((item) => item.category)),
  ];

  // Filtrer les compétences selon la catégorie sélectionnée
  const filteredSkills =
    activeCategory === "All"
      ? data.stack.item
      : data.stack.item.filter((item) => item.category === activeCategory);

  // Auto-scroll du carrousel
  useEffect(() => {
    const carousel = carouselRef.current;
    if (!carousel) return;

    const scrollInterval = setInterval(() => {
      if (carousel.scrollLeft + carousel.clientWidth >= carousel.scrollWidth) {
        // Retour au début
        carousel.scroll({ left: 0, behavior: "smooth" });
      } else {
        // Scroll de la largeur d'une carte
        const cardWidth =
          carousel.querySelector(".stack-card")?.clientWidth || 90;
        carousel.scroll({
          left: carousel.scrollLeft + cardWidth + 12,
          behavior: "smooth",
        });
      }
    }, 3500); // Scroll tous les 3.5 secondes

    return () => clearInterval(scrollInterval);
  }, [activeCategory, filteredSkills]);

  // Navigation manuelle
  const handleNavigation = (direction) => {
    const carousel = carouselRef.current;
    if (!carousel) return;

    const cardWidth = carousel.querySelector(".stack-card")?.clientWidth || 90;
    const scrollAmount = cardWidth + 12; // card width + gap

    if (direction === "left") {
      carousel.scroll({
        left: carousel.scrollLeft - scrollAmount,
        behavior: "smooth",
      });
    } else {
      carousel.scroll({
        left: carousel.scrollLeft + scrollAmount,
        behavior: "smooth",
      });
    }
  };

  return (
    <div className="stack">
      {/* Titre de la section */}
      <h3>Stack technique</h3>

      {/* Sélecteur de catégorie */}
      <div className="filter-dropdown-container">
        <select
          className="filter-dropdown"
          value={activeCategory}
          onChange={(e) => setActiveCategory(e.target.value)}
          aria-label="Filtrer les compétences par catégorie"
        >
          {categories.map((category) => (
            <option key={category} value={category}>
              {category}
            </option>
          ))}
        </select>
      </div>

      {/* Carrousel des compétences */}
      <div className="carousel-wrapper">
        {/* Bouton navigation gauche */}
        <motion.button
          className="carousel-nav carousel-nav-left"
          onClick={() => handleNavigation("left")}
          aria-label="Précédent"
          whileTap={tapPress}
          whileHover={{ scale: 1.05 }}
        >
          <FontAwesomeIcon icon={faChevronLeft} aria-hidden="true" />
        </motion.button>

        {/* Carrousel avec animation */}
        <motion.div
          ref={carouselRef}
          className="stack-items-carousel"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          viewport={sectionViewport}
          key={activeCategory}
        >
          {filteredSkills.map((skill, index) => (
            <motion.div
              key={skill.name}
              className="stack-card"
              variants={revealUp}
              custom={index}
              whileHover={{ y: -6, scale: 1.02 }}
              whileTap={tapPress}
            >
              {/* Icône ou initiale */}
              {parseIconClass(skill.icon) ? (
                <FontAwesomeIcon
                  icon={parseIconClass(skill.icon)}
                  aria-hidden="true"
                />
              ) : (
                <span className="icon-fallback" aria-label={skill.name}>
                  {skill.name[0]}
                </span>
              )}
              {/* Nom de la compétence */}
              <span className="skill-name">{skill.name}</span>
            </motion.div>
          ))}
        </motion.div>

        {/* Bouton navigation droite */}
        <motion.button
          className="carousel-nav carousel-nav-right"
          onClick={() => handleNavigation("right")}
          aria-label="Suivant"
          whileTap={tapPress}
          whileHover={{ scale: 1.05 }}
        >
          <FontAwesomeIcon icon={faChevronRight} aria-hidden="true" />
        </motion.button>
      </div>
    </div>
  );
}

export default Stack;
